// ----------------------------------------------------------------------
// <copyright file="Izenda.TopMenu.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { observable, action } from 'mobx';

// data imports
import { LICENSE_KEY } from 'logic/data/Izenda.LicenseCheckerConstants';
import {
  PERMISSION_KEY,
  PERMISSION_OPERATOR
} from 'logic/data/Izenda.AuthorizationConstants';
import uiState from 'logic/Izenda.UiState';

// share imports
import localizer from 'share/localization/Izenda.Localizer';
import SecurityManager from 'share/authorization/Izenda.SecurityManager';

// fetcher imports
import systemSettingFetcher from 'fetchers/Izenda.SystemSettingFetcher'

/**
 * @const TOP_MENU_ITEMS
 * @description list of all available menu options with initialized properties
 * @param {Object} menuItem main menu item that can have children that appear in a dropdown on desktop
 * @param {boolean} menuItem.isVisible indicator for whether the item should be visible
 * @param {string} menuItem.icon name of icon class that will appear with the title
 * @param {string} menuItem.key unique id for menu item
 * @param {string} menuItem.license name of the license needed to see this item in the menu
 * @param {boolean} menuItem.needDatabase indicator for if the user needs a database set up to see this item
 * @param {boolean} menuItem.needLicense indicator for if the user needs a license to view the item
 * @param {String[][]} menuItem.privilegeKeys nested array of strings that indiciate which priveleges the user needs
 * to view the item. The user must have at least 1 key from each of the inner arrays to view the item
 * @param {string} menuItem.title localized string that the user sees in the menu for the item
 * @param {object} menuItem.route contains name of routeUtils function to get the route and any params that function needs
 * @param {Object[]} menuItem.children list of menuItems that appear under the item in desktop view
 */
const TOP_MENU_ITEMS = [
  {
    isVisible: false,
    icon: 'icomoon-search',
    key: 'explore',
    license: LICENSE_KEY.Nlq,
    needDatabase: false,
    needLicense: true,
    get title(){return localizer.getText('TOP_MENU_EXPLORE')},
    route: {
      configFn: 'getExploreUrl',
      param: ''
    },
    privilegeKeys:[[PERMISSION_KEY.ReportsShowExploreMenuValue]],
    children: []
  },
  {
    icon: 'icomoon-folder',
    isVisible: false,
    key: 'report',
    needDatabase: true,
    needLicense: true,
    get title(){return localizer.getText('TOP_MENU_REPORTS')},
    route: {
      configFn: 'getReportUrl',
      param: ''
    },
    children: [
      {
        icon: 'icon-addnew',
        isHiddenOnMobile: true,
        isVisible: false,
        key: 'new_report',
        license: LICENSE_KEY.ReportDesigner,
        needDatabase: true,
        needLicense: true,
        privilegeKeys: [
          [
            PERMISSION_KEY.ReportsCanCreateNewReportValue,
          ],
          [
            PERMISSION_KEY.ReportsDaaSourcesSimpleDataSources,
            PERMISSION_KEY.ReportsDataSourcesAdvancedDataSources,
            PERMISSION_KEY.ReportsReportPartTypesChart,
            PERMISSION_KEY.ReportsReportPartTypesForm,
            PERMISSION_KEY.ReportsReportPartTypesGauge,
            PERMISSION_KEY.ReportsReportPartTypesMap,
            PERMISSION_KEY.ReportsFilterPropertiesFilterLogic,
            PERMISSION_KEY.ReportsFieldPropertiesCustomURL,
            PERMISSION_KEY.ReportsFieldPropertiesEmbeddedJavaScript,
            PERMISSION_KEY.ReportsFieldPropertiesSubreport,
            PERMISSION_KEY.ReportsActionsSchedule,
            PERMISSION_KEY.ReportsActionsConfigureAccessRights,
            PERMISSION_KEY.ReportsActionsEmail,
            PERMISSION_KEY.ReportsActionsPrint,
            PERMISSION_KEY.ReportsActionsExporting
          ]
        ],
        title: localizer.getText('TOP_MENU_NEW_REPORTS'),
        route: {
          configFn: 'getReportDesignerUrl',
          param: ''
        }
      },
    ]
  },
  {
    icon: 'icomoon-dashboard',
    isVisible: false,
    key: 'dashboard',
    license: LICENSE_KEY.Dashboard,
    needDatabase: true,
    needLicense: true,
    get title(){return localizer.getText('TOP_MENU_DASHBOARDS')},
    route: {
      configFn: 'getDashboardUrl',
      param: ''
    },
    children: [
      {
        route: {
          configFn: 'getDashboardUrl',
          param: 'New'
        },
        title: localizer.getText('TOP_MENU_NEW_DASHBOARDS'),
        icon: 'icon-addnew',
        isHiddenOnMobile: true,
        key: 'new_dashboard',
        needDatabase: true,
        needLicense: true,
        privilegeKeys: [[PERMISSION_KEY.DashboardsCanCreateNewDashboardValue]],
        license: LICENSE_KEY.Dashboard
      }
    ]
  },
  {
    icon: 'icomoon-settings',
    isHiddenOnMobile: true,
    isVisible: false,
    key: 'settings',
    needDatabase: false,
    needLicense: false,
    privilegeKeys: [[
      PERMISSION_KEY.TenantSetupActionsCreate,
      PERMISSION_KEY.TenantSetupActionsEdit,
      PERMISSION_KEY.TenantSetupActionsDel,
      PERMISSION_KEY.TenantSetupPermissionsValue,
      PERMISSION_KEY.SystemConfigurationScheduledInstancesValue,
      PERMISSION_KEY.DataSetupDataModelValue,
      PERMISSION_KEY.DataSetupAdvancedSettingsCategory,
      PERMISSION_KEY.DataSetupAdvancedSettingsOthers,
      PERMISSION_KEY.DataSetupDataConnectorsValue,
      PERMISSION_KEY.DataSetupImportValue,
      PERMISSION_KEY.UserSetupActionsCreate,
      PERMISSION_KEY.UserSetupActionsEdit,
      PERMISSION_KEY.UserSetupActionsDel,
      PERMISSION_KEY.UserSetupActionsConfigureSecurityOptions,
      PERMISSION_KEY.UserSetupUserRoleAssociationValue,
      PERMISSION_KEY.RoleSetupActionsCreate,
      PERMISSION_KEY.RoleSetupActionsEdit,
      PERMISSION_KEY.RoleSetupActionsDel,
      PERMISSION_KEY.RoleSetupDataModelAccessValue,
      PERMISSION_KEY.RoleSetupPermissionsValue,
      PERMISSION_KEY.RoleSetupGrantRoleWithFullReportAndDashboardAccessValue
    ]],
    get title(){return localizer.getText('TOP_MENU_SETTINGS')},
    route: {
      configFn: 'getSettingsUrl',
      param: ''
    },
    children: []
  }
];

/**
 * @class TopMenuStore
 * @description Store that controls and tracks the state of the top menu
 */
export default class TopMenuStore {
  @observable topMenu = [];
  @observable hasSetupDatabase = false;
  @observable focusedMenuKey = '';
  @observable logoImageWidth = 32;

  constructor() {
    this.authorization = new SecurityManager();
    this.getDatabaseConfigStatus();
  }

  /**
   * @method updateStoreBasedonPermissions
   * @description loops through `TOP_MENU_ITEMS` and creates an array of menuItems
   * and children that the user has access to
   */
  @action
  updateStoreBasedonPermissions() {
    this.topMenu = TOP_MENU_ITEMS.map(menuItem => {
      return {
        ...menuItem,
        isVisible: this.isMenuItemVisible(menuItem),
        children: menuItem.children.filter(child => this.isMenuItemVisible(child))
      }
    })
    .filter(menuItem => menuItem.isVisible);
  }

  /**
   * @method getDatabaseConfigStatus
   * @description fetch database configuration status
   */
  @action
  getDatabaseConfigStatus() {
    systemSettingFetcher.databaseSetup()
      .bind(this)
      .then(function(dbConfigured) {
        this.setHasSetupDatabase(dbConfigured);
      });
  }

  /**
   * @method setHasSetupDatabase
   * @description setter for `hasSetupDatabase`
   * @param {boolean} isSuccessful indicator for the user's database connection status
   */
  @action
  setHasSetupDatabase(isSuccessful) {
    this.hasSetupDatabase = isSuccessful;
    this.updateStoreBasedonPermissions();
  }

  @action
  setImageLogoWidth(width) {
    this.logoImageWidth = width < 220 ? width : 220;
  }

  /**
   * @method isMenuItemVisible
   * @description check if the user can view the item by checking the database config
   * permissions, the license permissions, and the role permissions
   * @param {Object} item menuItem whose permissions are being checked
   */
  isMenuItemVisible(item) {
    if (item.needDatabase && !this.hasSetupDatabase) {
      return false;
    }
    if (
      uiState.expiredLicense ||
      (
        item.needLicense &&
        item.license &&
        !this.authorization.checkLicense(item.license)
      )
    ) {
      return false;
    }
    if (
      item.privilegeKeys &&
      !item.privilegeKeys.reduce(
        (res, privileges) => res && this.authorization.checkPrivileges(privileges, PERMISSION_OPERATOR.Or),
        true
      )
    ) {
      return false;
    }
    return true;
  }
}



// WEBPACK FOOTER //
// ./ui/logic/Izenda.TopMenuStore.js